import React, { useEffect, useState } from 'react';
import {
    Paper, Typography, TextField, Button, Box, FormControlLabel, Switch,
    Grid, Card, CardContent, Divider, Stack, Slider, Chip, CircularProgress,
    useTheme, alpha
} from '@mui/material';
import { toast } from 'react-hot-toast';
import {
    FaLightbulb, FaTint, FaFan, FaSun, FaSeedling, FaClock,
    FaSave, FaSyncAlt, FaMicrochip, FaMapMarkerAlt
} from 'react-icons/fa';
import { api } from '../../services/api';
import { useApp } from '../../context/AppContext';
import DeviceManager from './DeviceManager';

const DEVICE_ID = 'esp32_001';

const LIGHT_PROFILES = [
    { value: 1, label: 'Vegetativo', icon: FaSeedling, color: '#66BB6A', desc: 'Luz azul, 18-24h' },
    { value: 2, label: 'Floração', icon: FaSun, color: '#FFB300', desc: 'Luz vermelha, 12h' },
    { value: 3, label: 'Clone', icon: FaSeedling, color: '#4DD0E1', desc: 'Suave, 18h' }
];

function AutomationCard({ icon: Icon, title, desc, checked, onChange, color }) {
    return (
        <Card sx={{
            height: '100%',
            background: checked ? alpha(color, 0.12) : 'rgba(255,255,255,0.03)',
            border: `1px solid ${checked ? color : 'rgba(255,255,255,0.08)'}`,
            transition: 'all .25s',
            '&:hover': { borderColor: color }
        }}>
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '50%', display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center',
                        bgcolor: checked ? color : 'rgba(255,255,255,0.08)',
                        color: checked ? '#fff' : 'text.secondary'
                    }}>
                        <Icon />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
                        <Typography variant="caption" color="text.secondary">{desc}</Typography>
                    </Box>
                    <Switch checked={checked} onChange={onChange} color="primary" />
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function Settings() {
    const theme = useTheme();
    const { selectedDeviceId } = useApp();
    const [config, setConfig] = useState({
        autoLighting: true, autoIrrigation: true, autoVentilation: true,
        lightingProfile: 1, irrigationAmount: 100, irrigationInterval: 12
    });
    const [device, setDevice] = useState({ location: '—', timezone: -3, status: '—' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!selectedDeviceId) return;
        setLoading(true);
        api.get(`/device/config?deviceId=${selectedDeviceId}`)
            .then(res => {
                setConfig(res.data.config);
                setDevice({ location: res.data.location, timezone: res.data.timezone, status: 'online' });
            })
            .catch(() => toast.error('Erro ao carregar configurações'))
            .finally(() => setLoading(false));
    }, [selectedDeviceId]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/device/config?deviceId=${selectedDeviceId}`, config);
            toast.success('Configurações salvas!');
        } catch (e) {
            toast.error('Erro ao salvar');
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setConfig({ autoLighting: true, autoIrrigation: true, autoVentilation: true, lightingProfile: 1, irrigationAmount: 100, irrigationInterval: 12 });
        toast('Valores restaurados', { icon: '↺' });
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
            {/* Gerenciador de dispositivos (até 10 placas) */}
            <DeviceManager />

            {/* Cabeçalho */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Box sx={{
                    width: 48, height: 48, borderRadius: '50%', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.main'
                }}>
                    <FaMicrochip size={22} />
                </Box>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>Configurações do Dispositivo</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" icon={<FaMapMarkerAlt />} label={device.location} variant="outlined" />
                        <Chip size="small" label={`Fuso: GMT${device.timezone >= 0 ? '+' : ''}${device.timezone}`} variant="outlined" />
                        <Chip size="small" icon={<FaSyncAlt />} label={selectedDeviceId} variant="outlined" color="primary" />
                    </Stack>
                </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Ajuste a automação do seu grow. As mudanças são enviadas ao ESP32 e aplicadas na próxima leitura.
            </Typography>

            <Grid container spacing={3}>
                {/* Automação */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%', background: 'rgba(255,255,255,0.02)' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Automação</Typography>
                        <Stack spacing={2}>
                            <AutomationCard icon={FaLightbulb} title="Iluminação automática" desc="Controla luz por perfil e fotoperíodo"
                                color="#FFB300" checked={config.autoLighting} onChange={e => setConfig({ ...config, autoLighting: e.target.checked })} />
                            <AutomationCard icon={FaTint} title="Rega automática" desc="Bomba dosada por intervalo"
                                color="#29B6F6" checked={config.autoIrrigation} onChange={e => setConfig({ ...config, autoIrrigation: e.target.checked })} />
                            <AutomationCard icon={FaFan} title="Ventilação automática" desc="Renovação de ar por temperatura/umidade"
                                color="#66BB6A" checked={config.autoVentilation} onChange={e => setConfig({ ...config, autoVentilation: e.target.checked })} />
                        </Stack>
                    </Paper>
                </Grid>

                {/* Iluminação */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%', background: 'rgba(255,255,255,0.02)' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <FaSun color="#FFB300" />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Perfil de Luz</Typography>
                        </Stack>
                        <Grid container spacing={1.5}>
                            {LIGHT_PROFILES.map(p => {
                                const active = config.lightingProfile === p.value;
                                return (
                                    <Grid item xs={4} key={p.value}>
                                        <Card onClick={() => setConfig({ ...config, lightingProfile: p.value })}
                                            sx={{
                                                cursor: 'pointer', textAlign: 'center', p: 1.5,
                                                border: `2px solid ${active ? p.color : 'transparent'}`,
                                                background: active ? alpha(p.color, 0.12) : 'rgba(255,255,255,0.03)',
                                                transition: 'all .2s'
                                            }}>
                                            <p.icon size={26} color={active ? p.color : '#888'} />
                                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>{p.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">{p.desc}</Typography>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                        <Box sx={{ mt: 3 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <FaSeedling color="#66BB6A" />
                                <Typography variant="body2">Intensidade do perfil</Typography>
                                <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 700, color: 'primary.main' }}>{config.lightingProfile}</Typography>
                            </Stack>
                            <Slider value={config.lightingProfile} min={1} max={3} step={1} marks
                                onChange={(e, v) => setConfig({ ...config, lightingProfile: v })}
                                sx={{ color: '#FFB300' }} />
                        </Box>
                    </Paper>
                </Grid>

                {/* Rega */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%', background: 'rgba(255,255,255,0.02)' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <FaTint color="#29B6F6" />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Irrigação</Typography>
                        </Stack>
                        <TextField label="Volume de rega" type="number" fullWidth margin="normal"
                            InputProps={{ endAdornment: <Typography variant="caption" sx={{ mr: 1 }}>mL</Typography> }}
                            value={config.irrigationAmount}
                            onChange={e => setConfig({ ...config, irrigationAmount: Math.max(0, +e.target.value) })} />
                        <Box sx={{ mt: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <FaClock color="#29B6F6" />
                                <Typography variant="body2">Intervalo entre regas</Typography>
                                <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 700, color: 'primary.main' }}>{config.irrigationInterval} h</Typography>
                            </Stack>
                            <Slider value={config.irrigationInterval} min={1} max={48} step={1}
                                onChange={(e, v) => setConfig({ ...config, irrigationInterval: v })} sx={{ color: '#29B6F6' }} />
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="caption" color="text.secondary">
                            💡 Bomba de 220 L/h ≈ 61 mL/s. Volume de {config.irrigationAmount} mL leva ~{(config.irrigationAmount / 61.11).toFixed(1)} s de acionamento.
                        </Typography>
                    </Paper>
                </Grid>

                {/* Resumo / Ações */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%', background: 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Resumo</Typography>
                        <Stack spacing={1.2}>
                            <SummaryRow label="Iluminação" value={config.autoLighting ? 'Automática' : 'Manual'} ok={config.autoLighting} />
                            <SummaryRow label="Rega" value={config.autoIrrigation ? 'Automática' : 'Manual'} ok={config.autoIrrigation} />
                            <SummaryRow label="Ventilação" value={config.autoVentilation ? 'Automática' : 'Manual'} ok={config.autoVentilation} />
                            <SummaryRow label="Perfil de luz" value={LIGHT_PROFILES.find(p => p.value === config.lightingProfile)?.label} />
                            <SummaryRow label="Rega" value={`${config.irrigationAmount} mL / ${config.irrigationInterval}h`} />
                        </Stack>
                        <Box sx={{ flex: 1 }} />
                        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                            <Button variant="outlined" startIcon={<FaSyncAlt />} onClick={reset} disabled={saving}>Restaurar</Button>
                            <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <FaSave />}
                                onClick={save} disabled={saving} sx={{ flex: 1 }}>{saving ? 'Salvando...' : 'Salvar alterações'}</Button>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

function SummaryRow({ label, value, ok }) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Chip size="small" label={value} color={ok === undefined ? 'default' : ok ? 'success' : 'default'}
                variant={ok ? 'filled' : 'outlined'} />
        </Stack>
    );
}
